"use client";

import { useMutation, useQueryClient } from "@tanstack/react-query";
import { createClient } from "@/lib/supabase/client";
import { queryKeys } from "@/lib/query/query-keys";
import { clientLogger } from "@/lib/client-logger";
import { identifyUser, trackEvent } from "@/lib/analytics/client";
import { PH } from "@/lib/analytics/events";

// ---------------------------------------------------------------------------
// useSignInWithPassword
// ---------------------------------------------------------------------------

export function useSignInWithPassword() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      email,
      password,
    }: {
      email: string;
      password: string;
    }) => {
      const supabase = createClient();
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) {
        clientLogger.error("[auth] sign-in failed", { err: error, flow: "password" });
        throw new Error(error.message);
      }

      return data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.auth.user() });
      const userId = data?.user?.id;
      if (userId) {
        identifyUser(userId, {
          email: data.user?.email ?? undefined,
          $email: data.user?.email ?? undefined,
        });
        trackEvent(PH.SIGNED_IN, { method: 'password' });
      }
    },
  });
}

// ---------------------------------------------------------------------------
// useSignUp
// ---------------------------------------------------------------------------

export function useSignUp() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      email,
      password,
      role,
      termsAccepted,
    }: {
      email: string;
      password: string;
      role: "employer" | "writer";
      termsAccepted: true;
    }) => {
      const supabase = createClient();
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          emailRedirectTo: `${window.location.origin}/auth/callback`,
          data: {
            role,
            terms_accepted: termsAccepted,
            terms_accepted_at: new Date().toISOString(),
          },
        },
      });

      if (error) {
        clientLogger.error("[auth] sign-up failed", { err: error, flow: "password" });
        throw new Error(error.message);
      }
      return data;
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.auth.user() });
      const userId = data?.user?.id;
      if (userId) {
        identifyUser(userId, {
          email: data.user?.email ?? undefined,
          $email: data.user?.email ?? undefined,
          role: variables.role,
        });
        trackEvent(PH.SIGNED_UP, {
          method: 'password',
          role: variables.role,
          // session is null when email confirmation is required.
          email_confirmation_required: !data.session,
        });
      }
    },
  });
}

// ---------------------------------------------------------------------------
// useResetPasswordForEmail
// ---------------------------------------------------------------------------

export function useResetPasswordForEmail() {
  return useMutation({
    mutationFn: async ({ email }: { email: string }) => {
      const supabase = createClient();
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/auth/reset`,
      });
      if (error) {
        clientLogger.error('[auth] reset-password email failed', { err: error, flow: 'recovery' });
        throw new Error(error.message);
      }
    },
  });
}

// ---------------------------------------------------------------------------
// useSignInWithGoogle
// ---------------------------------------------------------------------------

export function useSignInWithGoogle() {
  return useMutation({
    mutationFn: async ({ redirectTo }: { redirectTo?: string } = {}) => {
      const supabase = createClient();

      const callbackUrl = new URL("/auth/callback", window.location.origin);
      if (redirectTo) {
        callbackUrl.searchParams.set("next", redirectTo);
      }

      const { data, error } = await supabase.auth.signInWithOAuth({
        provider: "google",
        options: {
          redirectTo: callbackUrl.toString(),
        },
      });

      if (error) {
        clientLogger.error("[auth] Google sign-in failed", {
          err: error,
          flow: "google",
        });
        throw new Error(error.message);
      }
      return data;
    },
  });
}
